import { Navigate, Route, Routes, useParams } from "react-router";

import { SupervisorView } from "components/crane/supervisor/SupervisorView";
import { NotFound } from "components/notFound";
import { CraneDriver } from "components/crane/driver/CraneDriver";

import {
  RequireCranePagesEnabledPage,
  RequireCraneDriverAccess,
  RequireIsCraneSupervisor,
  RequireFeatureFlagsPage,
} from "./authentication";
import { RequireVersionRefresh } from "./dependencies";
import { CraneDriverProvider } from "components/crane/driver/CraneDriverProvider";
import { ErrorDetectorDialog } from "components/ConnectionDialog/ErrorDetectorDialog";
import { CraneHeatRecipes } from "components/crane/driver/CraneHeatRecipes";
import { Stack } from "@mui/material";
import { useTenantData } from "contexts/tenant";
import { OPERATIONS_FEATURE_FLAG } from "components/crane/featureFlags";

const ValidateSupervisorView = () => {
  const params = useParams<{
    deployment_type: string;
    plan_timeframe?: string;
  }>();

  if (
    params.plan_timeframe &&
    params.deployment_type === "standard" &&
    (params.plan_timeframe === "next" || params.plan_timeframe === "latest")
  ) {
    return (
      <SupervisorView
        deploymentType="standard"
        planTimeframe={params.plan_timeframe}
      />
    );
  } else if (params.deployment_type === "backup") {
    return <SupervisorView deploymentType="backup" />;
  } else {
    return <NotFound />;
  }
};

const CraneDriverPage = () => {
  const { crane_driver_view: craneDriverView } = useTenantData();

  switch (craneDriverView) {
    case "load_entry":
      return (
        <CraneDriverProvider>
          <ErrorDetectorDialog />
          <CraneDriver />
        </CraneDriverProvider>
      );
    case "heat_recipes":
      return (
        <Stack sx={{ p: 2, flexGrow: 1, minHeight: 0 }}>
          <ErrorDetectorDialog />
          <CraneHeatRecipes />
        </Stack>
      );
    case "none":
      return <NotFound />;
  }
};

export const CraneRoutes = () => {
  return (
    <Routes>
      <Route
        path="/supervisor/*"
        element={
          <RequireFeatureFlagsPage featureFlags={[OPERATIONS_FEATURE_FLAG]}>
            <RequireIsCraneSupervisor>
              <ErrorDetectorDialog />
              <Routes>
                <Route index element={<Navigate to="./standard/latest" />} />
                <Route
                  path="/active"
                  element={<Navigate to="../standard/latest" />}
                />
                <Route path="/standard" element={<Navigate to="./latest" />} />
                <Route
                  path="/:deployment_type/:plan_timeframe?"
                  element={<ValidateSupervisorView />}
                />
              </Routes>
            </RequireIsCraneSupervisor>
          </RequireFeatureFlagsPage>
        }
      />

      <Route
        path="/driver/*"
        element={
          <RequireCranePagesEnabledPage>
            <RequireVersionRefresh>
              <RequireCraneDriverAccess>
                <Routes>
                  <Route index element={<CraneDriverPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </RequireCraneDriverAccess>
            </RequireVersionRefresh>
          </RequireCranePagesEnabledPage>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
